/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Boundary } from '@floating-ui/react';

import { Typography2 } from '@coursera/cds-core';

import ModernTooltipWrapper from 'bundles/video-player/components/ModernTooltipWrapper';
import { playbackRatesTyped } from 'bundles/video-player/constants';
import type { PlaybackRate, VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';

import _t from 'i18n!nls/video-player';

const styles = {
  playbackRateControl: css`
    margin-right: var(--cds-spacing-200);
  `,
  indicatorText: css`
    font-weight: bold;
    font-size: var(--cds-font-size-300);
  `,
};

export type Props = {
  currentPlaybackRate: number | null;
  onPlaybackRateClick: (playbackRate: PlaybackRate) => void;
  videoBoundaryElement?: Boundary;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
};

const PlaybackRateControl = ({
  currentPlaybackRate,
  onPlaybackRateClick,
  videoBoundaryElement,
  trackVideoPlayerClickEvent,
}: Props) => {
  const handleClick = () => {
    // There should always be a current playback rate but to ensure it handles a potential
    // situation where it isn't loaded and to handle TS since the playback controls hook returns
    // it as number or null, this function is blocked from being
    // executed if it is undefined to prevent errors.
    if (!currentPlaybackRate) return;
    const currentIndex = playbackRatesTyped.indexOf(currentPlaybackRate);
    const nextIndex = (currentIndex + 1) % playbackRatesTyped.length;
    const nextRate = playbackRatesTyped[nextIndex];
    if (nextRate && currentPlaybackRate) {
      trackVideoPlayerClickEvent('video_playback_rate_switcher', {
        playbackRate: nextRate,
        previousPlaybackRate: currentPlaybackRate,
      });
    }
    onPlaybackRateClick(nextRate);
  };

  return (
    <ModernTooltipWrapper
      placement="top"
      message={_t('Switch Playback Rate')}
      keyboardShortcut="<"
      secondaryKeyboardShortcut=">"
      videoBoundaryElement={videoBoundaryElement}
      offset={22}
    >
      <button
        aria-label={_t('Video playback rate switcher')}
        type="button"
        onClick={handleClick}
        css={styles.playbackRateControl}
      >
        <Typography2 component="span" variant="titleSmall" color="invertBody" css={styles.indicatorText}>
          {currentPlaybackRate}x
        </Typography2>
      </button>
    </ModernTooltipWrapper>
  );
};

export default PlaybackRateControl;
